/**
 * DashboardHeader — modern top bar with gradient accent.
 */

import React from "react";
import {
  Box,
  Chip,
  IconButton,
  Paper,
  Snackbar,
  Tooltip,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import PrintIcon from "@mui/icons-material/Print";
import ShareIcon from "@mui/icons-material/Share";
import BugReportIcon from "@mui/icons-material/BugReport";
import BarChartIcon from "@mui/icons-material/BarChart";

import Brightness4Icon from "@mui/icons-material/Brightness4";
import Brightness7Icon from "@mui/icons-material/Brightness7";

interface DashboardHeaderProps {
  onDownloadCsv?: () => void;
  onPrint: () => void;
  /** Returns the shareable URL for the current view; header copies it to the
   *  clipboard and shows a confirmation toast. */
  onShare?: () => string;
  title: string;
  darkMode?: boolean;
  onToggleDark?: () => void;
}

const BUG_REPORT_URL =
  "https://form.asana.com/?k=YBdjTTPowR_mgOBaKXL7qQ&d=8442528107068";

const DashboardHeader: React.FC<DashboardHeaderProps> = ({
  onDownloadCsv,
  onPrint,
  onShare,
  title,
  darkMode,
  onToggleDark,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const [toastOpen, setToastOpen] = React.useState(false);
  const [toastMsg, setToastMsg] = React.useState("");

  const handleShare = async () => {
    if (!onShare) return;
    const url = onShare();
    try {
      await navigator.clipboard.writeText(url);
      setToastMsg("Link copied to clipboard");
    } catch {
      // Clipboard API can fail (permissions / non-secure context). Fall back
      // to a prompt so the user can still copy the link manually.
      window.prompt("Copy this link:", url);
      setToastMsg("");
    }
    if (navigator.clipboard) setToastOpen(true);
  };

  return (
    <>
      <Paper
        elevation={0}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 3,
          py: 2,
          borderRadius: 3,
          background: isDark
            ? "linear-gradient(135deg, #1a237e 0%, #283593 100%)"
            : "linear-gradient(135deg, #1565c0 0%, #1976d2 50%, #42a5f5 100%)",
          color: "#fff",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
          }}
        >
          <BarChartIcon sx={{ fontSize: 28, opacity: 0.9 }} />
          <Typography
            variant="h5"
            sx={{ fontWeight: 700, letterSpacing: "-0.02em" }}
          >
            {title}
          </Typography>
          <Chip
            label="FINN"
            size="small"
            sx={{
              bgcolor: "rgba(255,255,255,0.2)",
              color: "#fff",
              fontWeight: 600,
              fontSize: "0.7rem",
              height: 22,
            }}
          />
        </Box>

        <Box sx={{ display: "flex", gap: 0.5 }}>
          {[
            ...(onShare
              ? [
                  {
                    tip: "Share view",
                    icon: <ShareIcon />,
                    onClick: handleShare,
                  },
                ]
              : []),
            {
              tip: "Download CSV",
              icon: <FileDownloadIcon />,
              onClick: onDownloadCsv,
            },
            {
              tip: "Print",
              icon: <PrintIcon />,
              onClick: onPrint,
            },
            {
              tip: darkMode ? "Light mode" : "Dark mode",
              icon: darkMode ? <Brightness7Icon /> : <Brightness4Icon />,
              onClick: onToggleDark,
            },
            {
              tip: "Report a bug",
              icon: <BugReportIcon />,
              href: BUG_REPORT_URL,
            },
          ]
            .filter((btn) => btn.onClick || btn.href)
            .map((btn) => (
              <Tooltip key={btn.tip} title={btn.tip}>
                <IconButton
                  size="small"
                  onClick={btn.onClick}
                  {...(btn.href
                    ? {
                        component: "a",
                        href: btn.href,
                        target: "_blank",
                        rel: "noopener noreferrer",
                      }
                    : {})}
                  sx={{
                    color: "rgba(255,255,255,0.85)",
                    "&:hover": {
                      bgcolor: "rgba(255,255,255,0.15)",
                      color: "#fff",
                    },
                  }}
                  aria-label={btn.tip}
                >
                  {btn.icon}
                </IconButton>
              </Tooltip>
            ))}
        </Box>
      </Paper>
      <Snackbar
        open={toastOpen}
        autoHideDuration={2500}
        onClose={() => setToastOpen(false)}
        message={toastMsg}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
    </>
  );
};

export default DashboardHeader;
